import React, { useState, useEffect } from "react";
import RotaryLock from "./RotaryLock";

function useAnimatedData(base, variance, length = 20) {
  const [data, setData] = useState(() =>
    Array.from({ length }, (_, i) => ({ v: base + (Math.random() - 0.5) * variance * 2, t: i }))
  );
  useEffect(() => {
    const id = setInterval(() => {
      setData(prev => {
        const next = [...prev.slice(1)];
        next.push({ v: Math.max(0, Math.min(100, base + (Math.random() - 0.5) * variance * 2)), t: prev[prev.length - 1].t + 1 });
        return next;
      });
    }, 900);
    return () => clearInterval(id);
  }, [base, variance]);
  return data;
}

function Sparkline({ data, color, width, height, filled = false }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.v);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => ({
    x: (i / (vals.length - 1)) * width,
    y: height - ((v - min) / range) * (height - 6) - 3,
  }));
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const fillD = `${pathD} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width={width} height={height} style={{ overflow: "visible", display: "block" }}>
      {filled && <path d={fillD} fill={color} opacity="0.15" />}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
      <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="3.5"
        fill={color} style={{ filter: `drop-shadow(0 0 5px ${color})` }} />
    </svg>
  );
}

function CircleGauge({ value, color, label, size = 100 }) {
  const r     = (size - 16) / 2;
  const circ  = 2 * Math.PI * r;
  const dash  = Math.min(1, value / 100) * circ;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color})`, transition: "stroke-dasharray 0.7s" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "22px", fontWeight: "900", color, lineHeight: 1 }}>{Math.round(value)}%</div>
        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function BarChart({ data, color, width, height }) {
  if (!data || data.length === 0) return null;
  const vals = data.map(d => d.v);
  const max  = Math.max(...vals, 1);
  const bw   = width / vals.length - 2;
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {vals.map((v, i) => {
        const h = (v / max) * (height - 2);
        return (
          <rect key={i} x={i * (bw + 2)} y={height - h - 1} width={bw} height={h} rx="2"
            fill={color}
            opacity={i === vals.length - 1 ? 0.95 : 0.3 + (i / vals.length) * 0.5}
            style={i === vals.length - 1 ? { filter: `drop-shadow(0 0 4px ${color})` } : {}}
          />
        );
      })}
    </svg>
  );
}

const MENU_ITEMS = [
  { id: "overview",   label: "Vue d'ensemble", icon: "◈" },
  { id: "security",   label: "Sécurité",        icon: "⬡" },
  { id: "network",    label: "Réseau",           icon: "⬢" },
  { id: "biometrics", label: "Porte — Biométrie",icon: "◉" },
];

export default function MonitorDashboard({
  serverComplete, myVariant, playerCount, lockState, pseudoByVariant, onRotate, onTest, socketRef,
}) {
  const [activeMenu, setActiveMenu] = useState("overview");
  const [tick, setTick] = useState(0);
  // ── Indicateur de scroll — géré ici, affiché en fixed dans la modale
  const [showScrollHint, setShowScrollHint] = useState(false);

  const cpu    = useAnimatedData(63, 18, 20);
  const memory = useAnimatedData(71, 12, 20);
  const net    = useAnimatedData(42, 28, 20);
  const alerts = useAnimatedData(12, 8, 16);
  const latency= useAnimatedData(38, 22, 20);
  const io     = useAnimatedData(55, 25, 20);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Masquer l'indicateur quand on quitte la vue biométrie
  useEffect(() => {
    if (activeMenu !== "biometrics") setShowScrollHint(false);
  }, [activeMenu]);

  const lCpu  = cpu[cpu.length - 1]?.v ?? 0;
  const lMem  = memory[memory.length - 1]?.v ?? 0;
  const lNet  = net[net.length - 1]?.v ?? 0;
  const lAl   = alerts[alerts.length - 1]?.v ?? 0;
  const lLat  = latency[latency.length - 1]?.v ?? 0;
  const lIO   = io[io.length - 1]?.v ?? 0;

  const monitorUnlocked = lockState?.unlocked === true;

  return (
    <div style={{
      display: "flex", height: "100%", width: "100%",
      fontFamily: "'Courier New', monospace",
      background: "#07040a", color: "#e2d9f3", overflow: "hidden",
      position: "relative", // contexte pour le fixed indicator
    }}>

      {/* ── Indicateur de scroll — coin bas-droit de la modale ── */}
      {showScrollHint && activeMenu === "biometrics" && (
        <div style={{
          position: "absolute", bottom: 16, right: 16,
          pointerEvents: "none", zIndex: 100,
          animation: "scrollpulse 2s ease-in-out infinite",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            border: "1px solid #22d3ee55",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(2,8,16,0.90)",
            boxShadow: "0 0 12px #22d3ee25",
          }}>
            <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
              <path d="M1 1L5 5.5L9 1" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scrollpulse {
          0%, 100% { opacity: 0.35; transform: translateY(0px); }
          50% { opacity: 0.85; transform: translateY(3px); }
        }
      `}</style>

      {/* ── Sidebar ── */}
      <div style={{
        width: "80px", flexShrink: 0, background: "#0a060f",
        borderRight: "1px solid #1a0d2e",
        display: "flex", flexDirection: "column", alignItems: "center",
        paddingTop: 14, paddingBottom: 14, gap: 2,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, marginBottom: 16,
          background: "linear-gradient(135deg, #f97316, #ef4444)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "15px", boxShadow: "0 0 12px rgba(249,115,22,0.5)",
        }}>◉</div>

        {MENU_ITEMS.map(item => {
          const active = activeMenu === item.id;
          return (
            <button key={item.id} title={item.label} onClick={() => setActiveMenu(item.id)}
              style={{
                width: 70, padding: "8px 4px 6px", borderRadius: 8, border: "none",
                cursor: "pointer",
                background: active ? "rgba(249,115,22,0.18)" : "transparent",
                color: active ? "#f97316" : "#8a7a9c",
                boxShadow: active ? "0 0 10px rgba(249,115,22,0.3)" : "none",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: "20px", lineHeight: 1 }}>{item.icon}</span>
              <span style={{
                fontSize: "9px", fontWeight: "700", letterSpacing: "0.04em",
                textAlign: "center", lineHeight: 1.3,
                color: active ? "#f97316" : "#7a6a8c",
                textTransform: "uppercase", maxWidth: "64px", wordBreak: "break-word",
              }}>{item.label}</span>
            </button>
          );
        })}

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: monitorUnlocked ? "#34d399" : activeMenu === "biometrics" ? "#f97316" : "#2a1a3a",
            boxShadow: monitorUnlocked ? "0 0 8px #34d399" : "none",
            animation: !monitorUnlocked && activeMenu === "biometrics" ? "blink 1s step-end infinite" : "none",
          }} />
          <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.2}}`}</style>
        </div>
      </div>

      {/* ── Contenu ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{
          padding: "10px 20px", borderBottom: "1px solid #1a0d2e",
          background: "#09060e",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", boxShadow: "0 0 8px #f97316", animation: "blink 2s ease-in-out infinite" }} />
            <span style={{ fontSize: "12px", color: "#f97316", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: "700" }}>
              ORIGAMI MONITORING SYSTEM
            </span>
          </div>
          <div style={{ fontSize: "10px", color: "#f97316", letterSpacing: "0.12em", marginRight: "25px" }}>
            {MENU_ITEMS.find(m => m.id === activeMenu)?.label.toUpperCase()} — {new Date().toLocaleTimeString("fr-FR")}
          </div>
        </div>

        {/* ── VUE OVERVIEW ── */}
        {activeMenu === "overview" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
              {[
                { label: "CPU", value: lCpu, color: "#ef4444", data: cpu },
                { label: "MEM", value: lMem, color: "#f97316", data: memory },
                { label: "NET", value: lNet, color: "#fbbf24", data: net },
              ].map(kpi => (
                <div key={kpi.label} style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "10px", color: "#4a3a5c", letterSpacing: "0.18em", fontWeight: "700" }}>KPI</span>
                    <span style={{ fontSize: "13px", color: kpi.color, letterSpacing: "0.12em", fontWeight: "900" }}>{kpi.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <CircleGauge value={kpi.value} color={kpi.color} label={kpi.label} size={90} />
                    <div style={{ flex: 1 }}><Sparkline data={kpi.data} color={kpi.color} width={120} height={55} filled /></div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "16px", flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: "11px", color: "#4a3a5c", letterSpacing: "0.15em", fontWeight: "700" }}>ACTIVITÉ RÉSEAU — TEMPS RÉEL</span>
                <span style={{ fontSize: "20px", fontWeight: "900", color: "#f97316" }}>{lNet.toFixed(1)} <span style={{ fontSize: "10px", color: "#4a3a5c" }}>MB/s</span></span>
              </div>
              <Sparkline data={net} color="#f97316" width={600} height={80} filled />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "ALERTES / MIN", value: Math.round(lAl), color: "#ef4444", data: alerts, type: "bar" },
                { label: "LATENCE ms", value: Math.round(lLat), color: "#fbbf24", data: latency, type: "line" },
              ].map(kpi => (
                <div key={kpi.label} style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: "10px", color: "#4a3a5c", letterSpacing: "0.14em", fontWeight: "700" }}>{kpi.label}</span>
                    <span style={{ fontSize: "22px", fontWeight: "900", color: kpi.color }}>{kpi.value}</span>
                  </div>
                  {kpi.type === "bar" ? <BarChart data={kpi.data} color={kpi.color} width={240} height={55} /> : <Sparkline data={kpi.data} color={kpi.color} width={240} height={55} filled />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── VUE SÉCURITÉ ── */}
        {activeMenu === "security" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "INTÉGRITÉ SYSTÈME", value: 100 - lAl * 0.8, color: "#34d399" },
                { label: "NIVEAU DE MENACE", value: lAl * 2.4, color: "#ef4444" },
                { label: "CHIFFREMENT", value: 98, color: "#818cf8" },
                { label: "AUTHENTIFICATION", value: lCpu * 0.6 + 20, color: "#f97316" },
              ].map(kpi => (
                <div key={kpi.label} style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "20px", display: "flex", alignItems: "center", gap: 20 }}>
                  <CircleGauge value={Math.max(0, Math.min(100, kpi.value))} color={kpi.color} label="" size={90} />
                  <div>
                    <div style={{ fontSize: "10px", color: "#4a3a5c", letterSpacing: "0.14em", marginBottom: 6, fontWeight: "700" }}>{kpi.label}</div>
                    <div style={{ fontSize: "32px", fontWeight: "900", color: kpi.color }}>{Math.round(Math.max(0, Math.min(100, kpi.value)))}%</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "16px" }}>
              <div style={{ fontSize: "11px", color: "#4a3a5c", letterSpacing: "0.15em", marginBottom: 12, fontWeight: "700" }}>LOG DE SÉCURITÉ — DERNIERS ÉVÉNEMENTS</div>
              {[
                { t: "14:22:07", msg: "Tentative d'accès biométrique — REJETÉE", c: "#ef4444" },
                { t: "14:21:55", msg: "Scan périmétrique terminé — 3 anomalies", c: "#fbbf24" },
                { t: "14:21:30", msg: "Module de surveillance actif", c: "#34d39980" },
                { t: "14:20:11", msg: "Recalibration porte — EN ATTENTE", c: "#f9731660" },
              ].map((log, i) => (
                <div key={i} style={{ display: "flex", gap: 14, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #0f0718" }}>
                  <span style={{ fontSize: "10px", color: "#2a1a3a", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{log.t}</span>
                  <span style={{ fontSize: "11px", color: log.c, fontWeight: "600" }}>{log.msg}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── VUE RÉSEAU ── */}
        {activeMenu === "network" && (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "16px" }}>
              <div style={{ fontSize: "11px", color: "#4a3a5c", letterSpacing: "0.15em", marginBottom: 12, fontWeight: "700" }}>DÉBIT RÉSEAU — ENTRÉE / SORTIE</div>
              <div style={{ display: "flex", gap: 30, marginBottom: 14 }}>
                {[{ label: "↓ ENTRÉE", val: lNet, color: "#f97316" }, { label: "↑ SORTIE", val: lIO, color: "#ef4444" }].map(d => (
                  <div key={d.label}>
                    <div style={{ fontSize: "10px", color: "#4a3a5c", marginBottom: 4, fontWeight: "700" }}>{d.label}</div>
                    <div style={{ fontSize: "28px", fontWeight: "900", color: d.color }}>{d.val.toFixed(1)} <span style={{ fontSize: "12px" }}>MB/s</span></div>
                  </div>
                ))}
              </div>
              <Sparkline data={net} color="#f97316" width={600} height={70} filled />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "I/O DISQUE", data: io, color: "#818cf8", type: "bar" },
                { label: "PAQUETS PERDUS %", data: alerts.map(d => ({...d, v: d.v * 0.3})), color: "#fbbf24", type: "line" },
              ].map(kpi => (
                <div key={kpi.label} style={{ background: "#0c0712", border: "1px solid #1a0d2e", borderRadius: 8, padding: "14px" }}>
                  <div style={{ fontSize: "10px", color: "#4a3a5c", letterSpacing: "0.12em", marginBottom: 10, fontWeight: "700" }}>{kpi.label}</div>
                  {kpi.type === "bar" ? <BarChart data={kpi.data} color={kpi.color} width={240} height={60} /> : <Sparkline data={kpi.data} color={kpi.color} width={240} height={60} filled />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── VUE BIOMÉTRIE ── */}
        {activeMenu === "biometrics" && (
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{
              padding: "8px 20px", background: "#0a060f",
              borderBottom: "1px solid #1a0d2e",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: monitorUnlocked ? "#34d399" : "#f97316",
                  boxShadow: monitorUnlocked ? "0 0 8px #34d399" : "0 0 8px #f97316",
                  animation: !monitorUnlocked ? "blink 1s step-end infinite" : "none",
                }} />
                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.18em", color: monitorUnlocked ? "#34d399" : "#f97316" }}>
                  {monitorUnlocked ? "PAD BIOMÉTRIQUE — RECONFIGURÉ" : "PAD BIOMÉTRIQUE — RECONFIGURATION REQUISE"}
                </span>
              </div>
              {!serverComplete && (
                <span style={{ fontSize: "10px", color: "#2a1a3a", letterSpacing: "0.1em", fontWeight: "600" }}>
                  ACCÈS RESTREINT — TRANSFÉRER LES PREUVES D'ABORD
                </span>
              )}
            </div>

            {!serverComplete ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
                <div style={{ fontSize: "40px" }}>🔒</div>
                <div style={{ fontSize: "13px", color: "#2a1a3a", letterSpacing: "0.2em", textAlign: "center", fontWeight: "700" }}>
                  ACCÈS RESTREINT<br />
                  <span style={{ fontSize: "10px", opacity: 0.6, fontWeight: "400" }}>Transférer les preuves sur le serveur d'abord</span>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
                <RotaryLock
                  myVariant={myVariant}
                  playerCount={playerCount}
                  lockState={lockState}
                  pseudoByVariant={pseudoByVariant}
                  onRotate={onRotate}
                  onTest={onTest}
                  socketRef={socketRef}
                  onScrollHintChange={setShowScrollHint}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}